import {isEmpty, isString} from "radash";
import {erNil} from "./nilsjekk";
import {erTomListe, første, siste} from "felles-ng2/støttefunksjoner/lister";
import {nil} from "felles-ng2/typer/nullish";

export function erStreng(verdi: any): verdi is string {
    return isString(verdi);
}

/** sann dersom angitte strenger er like når man ignorerer casing, strengene trimmes før sammenlikning*/
export function erLikIgnorerCasing(a: string | nil, b: string | nil): boolean {
    if (a === b) return true;
    if (erNil(a) || erNil(b)) return false; //enten a eller b er null/undefined, men ikke begge
    return a.trim()!.toLowerCase() == b.trim()!.toLowerCase();
}

export function trimTilNull(streng: string | nil): string | null {
    if (erNil(streng)) return null;
    streng = streng.trim();
    if (isEmpty(streng)) return null;
    return streng;
}

export function trimTilTomStreng(streng: string | nil): string {
    return trimTilNull(streng) ?? '';
}

export function erTomEllerNil(streng: string | nil): streng is null | undefined {
    return trimTilNull(streng) === null;
}

export function erIkkeTomStreng(streng: string | nil): streng is string {
    return !erTomEllerNil(streng);
}

export function erStrengSomSlutterMed(streng: any, suffix: string): boolean {
    if (!erStreng(streng)) return false
    return streng.endsWith(suffix);
}

export function standardStreng(potensiellTom: string | nil, standard: string) {
    return erTomEllerNil(potensiellTom) ? standard : potensiellTom;
}

/** Returnerer første ikke-tomme streng, eller '' dersom alle er null eller tomme */
export function førsteIkkeTommeStreng(...strenger: (string | nil)[]): string {
    return strenger.find(erIkkeTomStreng) ?? '';
}

const htmlTagRegexp = /<[^>]+>/gm;

export function fjernHtml(html: string) {
    if (html == null) return html;
    return html.replace(htmlTagRegexp, '');
}

export function initialer(navn: string | nil): string {
    if (erTomEllerNil(navn)) return '';
    var ord = navn.trim().split(" ")
      .filter(del => erIkkeTomStreng(del))
      .map(del => del[0].toUpperCase());
    return første(ord)! + (ord.length > 1 ? siste(ord) : '');
}

export function førsteOrd(tekst: string | nil): string {
    if (erTomEllerNil(tekst)) return '';
    return tekst.trim().split(' ')[0];
}

export function listeTilStreng(liste: string[], skilletegn: string, sisteSkilletegn: string): string {
    if (erTomListe(liste)) return '';

    return liste
      .filter(innslag => erIkkeTomStreng(innslag))
      .reduce((listeSomStreng, innslag, indeks, liste) => {
            var skille = '';
            if (indeks == 0) skille = '';
            else if (indeks == liste.length - 1) skille = ` ${sisteSkilletegn} `;
            else skille = `${skilletegn} `;
            return listeSomStreng + skille + innslag;
        },
        '');
}

/** Collator for bruk ved sortering der vi ønsker at tall i tekst skal sorteres naturlig og casing skal ignoreres.
 * Se https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/Collator/Collator#options */
const alfanumeriskCollator = new Intl.Collator(undefined, {
    //Whether numeric collation should be used, such that "1" < "2" < "10". - sørger for naturlig sortering av tall
    numeric: true,
    //Only strings that differ in base letters compare as unequal. Examples: a ≠ b, a = á, a = A - sørger for å ignorere casing
    sensitivity: 'base'
});

/** Sammenlikner angitte strenger uten å ta hensyn til casing, tar hensyn til tall i strengen slik at "abc 9" kommer før "abc 10".
 * Se no.systemfabrikken.lederplan.delt.Streng.sammenliknAlfanumerisk for tilsvarende sortering på Java-siden.
 * @return 0 dersom a == b, mindre enn 0 dersom a kommer før b, større enn 0 dersom a kommer etter b */
export function sammenliknAlfanumerisk(a: string, b: string): number {
    return alfanumeriskCollator.compare(a, b)
}
